import { XMarkIcon } from '@heroicons/react/24/outline';
import * as SheetPrimitive from '@radix-ui/react-dialog';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

/** Off-canvas panel (mobil drawer) a Radix Dialog-primitíven — fókusz-csapda + Escape + a11y ingyen. */
const Sheet = SheetPrimitive.Root;
const SheetTrigger = SheetPrimitive.Trigger;
const SheetClose = SheetPrimitive.Close;

const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content> & {
    side?: 'left' | 'right';
    /** A beépített, jobb-felső abszolút záró-X elrejtése — ha a hívó a fejléc-sorában adja a záró gombot
     *  (a címmel egy vonalban, a dialog-minta szerint). Alap: false (a régi drawer-viselkedés marad). */
    hideClose?: boolean;
  }
>(({ side = 'left', className, children, hideClose = false, ...props }, ref) => {
  const { t } = useTranslation();
  return (
    <SheetPrimitive.Portal>
      <SheetPrimitive.Overlay className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 app-fixed-fill fixed inset-0 z-50 bg-black/50" />
      <SheetPrimitive.Content
        ref={ref}
        className={cn(
          'dark-surface-popover data-[state=open]:animate-in data-[state=closed]:animate-out app-fixed-fill fixed inset-y-0 z-50 flex flex-col transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-300',
          side === 'left'
            ? // BAL: navigációs drawer (mobil menü) — keskeny, a tartalom mögé csúszik.
              'data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left left-0 w-72 max-w-[85vw] border-r'
            : // JOBB: a MUNKA-panel (hozzáadás/szerkesztés, részletnézet, Mo) — EGY szabály az egész appban:
              // mobilon a teljes képernyő, `sm`-től a viewport 40%-a, de sosem keskenyebb 32rem-nél és sosem
              // szélesebb 48rem-nél (`clamp`). A hívó a szélességet NEM írja felül (fal:
              // `sheet-width.wall.spec.ts`), tehát ha ezen változtatni kell, az ITT egy sor, és MINDEN panel
              // együtt mozdul.
              //
              // ⚠️ MIÉRT ARÁNYOS, ÉS MIÉRT NEM TÖRÉSPONTOS (user-lelet, két körben):
              // Előbb fix lépcsők voltak (`lg` → 40rem, `xl` → 48rem). A Tailwind töréspontjai ehhez túl
              // alacsonyan vannak: egy 14" MacBook logikai szélessége 1512 px, egy 16"-é 1728 — mindkettő
              // `xl` FÖLÖTT, tehát a LAPTOP is a legszélesebb fokozatot kapta, a képernyő felét („rohadt
              // széles lett"). Ugyanaz a 768 px egy 27"-esen (2560 px) viszont már csak ~30%, és ott jó.
              // Egy töréspont nem tudja a kettőt szétválasztani, mert a `2xl` is csak 1536-nál van.
              // A `clamp` igen: 1512 px-en 605 px, 1728-on 691, 2560-on és fölötte 768.
              //
              // A `max-w-[92vw]` sapka marad: keskeny ablakban a panel sosem lóg ki, és a mögötte lévő lista
              // mindig marad egy csíknyi kapaszkodónak.
              'data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right right-0 w-full max-w-full border-l sm:w-[clamp(32rem,40vw,48rem)] sm:max-w-[92vw]',
          className,
        )}
        {...props}
      >
        {!hideClose && (
          // A ZÁRÓ „X" a FEJLÉC-CÍM KÖZEPÉVEL egy vonalban (user-lelet): a 16px-es ikon `top-3`-mal a 20.
          // pixelre esett, a `p-4`/`py-4` fejlécek `text-base` címe viszont a 28.-ra — az „X" észrevehetően
          // magasabban állt. A `size-6`-os, középre igazító kattintó-felület a 28. pixelre teszi (16px
          // padding + a 24px-es box fele), és egyben nagyobb célt is ad az ujjnak.
          // `z-10`: az X a tartalom ELŐTT renderel, így egy utána jövő ELHELYEZETT (`relative`) fejléc a festési
          // sorrend miatt rátakart, és elkapta a kattintást (kolléga-lelet: a portál üzenet-sheetje csak
          // mellékattintással zárt) — a záró gomb mindig a sheet tartalma fölött van.
          <SheetPrimitive.Close className="absolute right-3 top-4 z-10 flex size-6 items-center justify-center rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none">
            <XMarkIcon className="size-4" />
            <span className="sr-only">{t('common.close')}</span>
          </SheetPrimitive.Close>
        )}
        {children}
      </SheetPrimitive.Content>
    </SheetPrimitive.Portal>
  );
});
SheetContent.displayName = 'SheetContent';

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title ref={ref} className={cn('text-base font-semibold', className)} {...props} />
));
SheetTitle.displayName = 'SheetTitle';

export { Sheet, SheetTrigger, SheetClose, SheetContent, SheetTitle };
